/**
 * Data Prep Service — wraps /api/admin/ai/data-prep/
 * Phase 4.8-B — WQ-198
 */

// === Types ===

export interface Transform {
  name: string;
  category: string;
  description: string;
  parameters: Record<string, unknown>;
}

export interface Recipe {
  name: string;
  description: string | null;
  steps: RecipeStep[];
  created_at: string;
  updated_at: string;
}

export interface RecipeStep {
  transform: string;
  parameters: Record<string, unknown>;
  order: number;
}

export interface PreviewResult {
  columns: string[];
  sample_rows: Record<string, unknown>[];
  row_count: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function listTransforms(authFetch: FetchFn): Promise<Transform[]> {
  const res = await authFetch('/api/admin/ai/data-prep/transforms');
  if (!res.ok) throw new Error(`Failed to list transforms: ${res.status}`);
  return res.json();
}

export async function previewTransform(
  authFetch: FetchFn,
  body: { transform: string; parameters: Record<string, unknown>; sample_data?: unknown }
): Promise<PreviewResult> {
  const res = await authFetch('/api/admin/ai/data-prep/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to preview transform: ${res.status}`);
  return res.json();
}

export async function createRecipe(
  authFetch: FetchFn,
  data: { name: string; description?: string; steps: RecipeStep[] }
): Promise<Recipe> {
  const res = await authFetch('/api/admin/ai/data-prep/recipes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create recipe: ${res.status}`);
  return res.json();
}

export async function listRecipes(authFetch: FetchFn): Promise<Recipe[]> {
  const res = await authFetch('/api/admin/ai/data-prep/recipes');
  if (!res.ok) throw new Error(`Failed to list recipes: ${res.status}`);
  return res.json();
}

export async function getRecipe(authFetch: FetchFn, name: string): Promise<Recipe> {
  const res = await authFetch(`/api/admin/ai/data-prep/recipes/${encodeURIComponent(name)}`);
  if (!res.ok) throw new Error(`Failed to get recipe: ${res.status}`);
  return res.json();
}

export async function updateRecipe(
  authFetch: FetchFn,
  name: string,
  data: Partial<Recipe>
): Promise<Recipe> {
  const res = await authFetch(`/api/admin/ai/data-prep/recipes/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update recipe: ${res.status}`);
  return res.json();
}

export async function deleteRecipe(authFetch: FetchFn, name: string): Promise<void> {
  const res = await authFetch(`/api/admin/ai/data-prep/recipes/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to delete recipe: ${res.status}`);
}

export async function previewRecipe(
  authFetch: FetchFn,
  name: string,
  body?: { sample_data?: unknown }
): Promise<PreviewResult> {
  const res = await authFetch(`/api/admin/ai/data-prep/recipes/${encodeURIComponent(name)}/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(`Failed to preview recipe: ${res.status}`);
  return res.json();
}

export async function validateRecipe(
  authFetch: FetchFn,
  name: string
): Promise<ValidationResult> {
  const res = await authFetch(`/api/admin/ai/data-prep/recipes/${encodeURIComponent(name)}/validate`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to validate recipe: ${res.status}`);
  return res.json();
}
